import React, { Component, ErrorInfo, ReactNode } from 'react'
import { Card } from './ui/card'
import { UnifiedButton } from './ui/unified-button'
import { AlertTriangle } from 'lucide-react'
import { log } from '../lib/logger'

interface Props {
  children: ReactNode
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Error boundary component to catch and display errors gracefully
 * Prevents the entire app from crashing due to component errors
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error securely without exposing sensitive information
    const sanitizedError = {
      message: error.message,
      name: error.name,
      componentStack: errorInfo.componentStack?.substring(0, 1000), // Limit stack trace size
      errorBoundary: this.constructor.name
    }

    // Use secure logging system
    log.error(
      'React component error caught by error boundary',
      error,
      sanitizedError,
      'ERROR_BOUNDARY'
    )

    // Fallback to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Uncaught error:', error, errorInfo)
    }
  }

  private resetError = () => {
    this.setState({ hasError: false, error: null })
  }

  public render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.resetError)
      }

      // Default error UI
      return (
        <div className="flex min-h-screen items-center justify-center p-4">
          <Card className="w-full max-w-md p-6">
            <div className="mb-4 flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-red-500" />
              <h2 className="text-xl font-semibold">Something went wrong</h2>
            </div>

            <p className="text-muted-foreground mb-4">
              An unexpected error occurred. The error has been logged.
            </p>

            <details className="mb-4">
              <summary className="mb-2 cursor-pointer text-sm font-medium">Error details</summary>
              <pre className="bg-secondary overflow-auto rounded p-3 text-xs">
                {this.state.error.stack || this.state.error.message}
              </pre>
            </details>

            <div className="flex gap-2">
              <UnifiedButton onClick={this.resetError} variant="primary" size="sm">
                Try again
              </UnifiedButton>
              <UnifiedButton onClick={() => window.location.reload()} variant="outline" size="sm">
                Reload page
              </UnifiedButton>
            </div>
          </Card>
        </div>
      )
    }

    return this.props.children
  }
}
